import {
  Exhibition,
  ItinerantExhibitionParentType,
} from "@/lib/types";
import { ReactElement, ReactNode, cloneElement, isValidElement } from "react";
import { itinerantStopFromExhibition } from "./ItinerantExhibitionPanel";
import { ItinerantDrawerConfig } from "./itinerant-drawer-types";

type LinkLike = {
  id: string;
  exhibition: Exhibition;
  itinerantGroupId?: string | null;
  itinerantOrder?: number | null;
  itinerantNote?: string;
};

type ComplexItinerantGateProps<T extends LinkLike> = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentType: ItinerantExhibitionParentType;
  parentId: string;
  exhibition: Exhibition;
  existingLink?: T | null;
  allLinks?: T[];
  complexRelationKey: string;
  getCatalogNumber?: (link: T) => string;
  getExemplaireExpose?: (link: T) => string;
  showCatalogNumber?: boolean;
  showExemplaireExpose?: boolean;
  /** Classic *InExhibition drawer — receives itinerantConfig when needed */
  classic: ReactNode;
};

function isItinerantFlag(
  value: boolean | string | number | undefined | null,
): boolean {
  return (
    value === true || value === "true" || value === "t" || value === 1
  );
}

/**
 * Injects itinerantConfig into the existing create/edit link drawer when the
 * exhibition is itinerant (or the link already belongs to a group). Does not
 * replace the drawer.
 */
export function ComplexItinerantGate<T extends LinkLike>({
  open,
  onOpenChange,
  parentType,
  parentId,
  exhibition,
  existingLink,
  allLinks,
  complexRelationKey,
  getCatalogNumber,
  getExemplaireExpose,
  showCatalogNumber = true,
  showExemplaireExpose = false,
  classic,
}: ComplexItinerantGateProps<T>) {
  const groupId =
    existingLink?.itinerantGroupId ||
    allLinks?.find((l) => l.id === existingLink?.id)?.itinerantGroupId ||
    allLinks?.find((l) => l.exhibition?.id === exhibition.id)
      ?.itinerantGroupId ||
    null;
  const isItinerant =
    Boolean(groupId) ||
    isItinerantFlag(exhibition.expositionsDescriptionExpositionItinerante);

  if (!isValidElement(classic)) {
    return <>{classic}</>;
  }

  if (!isItinerant) {
    return cloneElement(classic as ReactElement, { open, onOpenChange });
  }

  const initialStops =
    groupId && allLinks
      ? allLinks
          .filter((l) => l.itinerantGroupId === groupId)
          .sort((a, b) => (a.itinerantOrder ?? 0) - (b.itinerantOrder ?? 0))
          .map((l) =>
            itinerantStopFromExhibition(l.exhibition, l.id, {
              catalogNumber: getCatalogNumber?.(l) ?? "",
              exemplaireExpose: getExemplaireExpose?.(l) ?? "",
              note: l.itinerantNote ?? "",
            }),
          )
      : undefined;

  const itinerantConfig: ItinerantDrawerConfig = {
    parentType,
    parentId,
    groupId,
    seedExhibition: exhibition,
    initialStops,
    complexRelationKey,
    showCatalogNumber,
    showExemplaireExpose,
  };

  return cloneElement(classic as ReactElement, {
    open,
    onOpenChange,
    itinerantConfig,
  });
}
